// Interface for month options
interface MonthOption {
    value: string;
    label: string;
}

export const formatDate = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

export const formatDateForInput = (date: string | Date | null | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Utility function to safely format dates as DD/MM/YYYY
export const formatDisplayDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) return ''; // Return an empty string for null or undefined dates
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`; // Convert YYYY-MM-DD to DD/MM/YYYY
};

export function getCurrentDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export const parseDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);

    // Validate ranges before creating date
    if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1000 || year > 9999) {
        return null;
    }

    const date = new Date(year, month, day);
    
    // Check if the date rolled over (e.g., Feb 30 becomes Mar 2)
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
        return null;
    }

    return isNaN(date.getTime()) ? null : date;
};

/**
 * Converts a date string from "dd-MM-yyyy" format to a Date object.
 * @param {string} dateString - The date string in "dd-MM-yyyy" format.
 * @returns {Date | null} A Date object representing the given date, or null if the input is invalid.
 */
function convertDateStringToDate(dateString: string | null | undefined): Date | null {
    if (!dateString || !/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        console.error("Invalid date string format. Expected format: dd-MM-yyyy");
        return null;
    }

    const parts = dateString.split('-').map(part => parseInt(part, 10));
    const day = parts[0];
    const month = parts[1] - 1; // Month is 0-indexed in JavaScript Date
    const year = parts[2];

    // Validate ranges before creating date
    if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1000 || year > 9999) {
        console.error("Invalid date components. Conversion resulted in an invalid date.");
        return null;
    }

    const date = new Date(year, month, day);
    
    // Check if the date rolled over (e.g., Feb 30 becomes Mar 2)
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
        console.error("Invalid date components. Conversion resulted in an invalid date.");
        return null;
    }

    if (isNaN(date.getTime())) {
        console.error("Invalid date components. Conversion resulted in an invalid date.");
        return null;
    }

    return date;
}

export const todayUk = (): string => {
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date().toLocaleDateString('en-GB', options);
};

export const getMonthOptions = (startDate?: Date | null, endDate: Date = new Date()): MonthOption[] => {
    if (!startDate) {
        const today = new Date();
        startDate = new Date(today.getFullYear(), 0, 1);
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        const today = new Date();
        return [{
            value: `${today.getFullYear()}-01`,
            label: 'January ' + today.getFullYear()
        }];
    }
    
    const options: MonthOption[] = [];
    const currentDate = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (currentDate <= end) {
        const monthName = currentDate.toLocaleString('en-GB', { month: 'long' });
        const year = currentDate.getFullYear();
        
        options.push({
            value: `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
            label: `${monthName} ${year}`
        });
        
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return options;
};

export const getLastDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
};

export const getMonthEndDate = (monthValue: string | null | undefined): Date => {
    if (!monthValue || typeof monthValue !== 'string' || monthValue.includes('NaN')) {
        return new Date();
    }
    
    const parts = monthValue.split('-');
    if (parts.length !== 2) {
        return new Date();
    }
    
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    
    if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
        return new Date();
    }
    
    const currentDate = new Date();
    
    if (currentDate.getFullYear() === year && currentDate.getMonth() + 1 === month) {
        return currentDate;
    }
    
    const lastDay = getLastDayOfMonth(year, month);
    const endDate = new Date(year, month - 1, lastDay);
    
    return isNaN(endDate.getTime()) ? new Date() : endDate;
};

export default convertDateStringToDate; 